/**
 * Login Page — design from the Claude Design auth handoff.
 *
 * Split brand-hero layout (centered card on small screens),
 * inline email validation, show/hide password, remember-me.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { billingApi } from '../services/api'
import { consumePendingCheckout } from '../utils/pendingCheckout'
import { IS_NATIVE_APP } from '../services/runtime'
import AuthLayout from '../components/AuthLayout'
import {
  Field, PasswordField, Checkbox, PrimaryButton, ErrorBanner,
  FormHeader, TextLink, FootSwitch, validators, MailIcon,
} from '../components/AuthFields'
import { JAI_COLORS, JAI_FONTS } from '../components/JaiBrand'

export default function Login() {
  const { login } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [pw, setPw] = useState('')
  const [remember, setRemember] = useState(true)
  const [touched, setTouched] = useState({})
  const [formErr, setFormErr] = useState('')
  const [loading, setLoading] = useState(false)

  const eErr = touched.email ? validators.email(email) : ''
  const pErr = touched.pw && !pw ? 'Password is required' : ''

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Read the live DOM values rather than trusting email/pw state alone —
    // mobile autofill (Face ID, Keychain suggestions) can fill a field
    // visually without firing React's onChange, which left this state empty
    // and made the guard below silently block sign-in with zero feedback.
    const formEmail = e.target.elements.email.value.trim()
    const formPw = e.target.elements.password.value
    if (formEmail !== email) setEmail(formEmail)
    if (formPw !== pw) setPw(formPw)

    setTouched({ email: true, pw: true })
    if (validators.email(formEmail) || !formPw) return

    setFormErr('')
    setLoading(true)
    try {
      await login(formEmail, formPw)

      // An existing user who clicked a paid plan while signed out lands here
      // via Signup's "Sign in" link — resume the checkout they started
      // rather than dropping them in the app with no memory of it. See
      // Pricing.jsx / utils/pendingCheckout.js. There is no Pricing page and
      // nothing sells anything on native (Guideline 3.1.1), so this whole
      // resume path is skipped there — in practice nothing ever sets a
      // pending checkout on native anyway, since Pricing is unreachable.
      if (!IS_NATIVE_APP) {
        const pending = await consumePendingCheckout()
        if (pending) {
          try {
            const { data } = await billingApi.startCheckout(pending.planId, pending.billing)
            window.location.href = data.url
            return
          } catch {
            navigate('/pricing')
            return
          }
        }
      }

      navigate('/chat')
    } catch (err) {
      setFormErr(err.response?.data?.error || 'Login failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout screen="login">
      <form onSubmit={handleSubmit} noValidate>
        <FormHeader title="Welcome back" sub="Sign in to pick up where your leagues left off." />
        <ErrorBanner>{formErr}</ErrorBanner>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Field label="Email address" type="email" name="email" value={email} autoFocus autoComplete="email"
            onChange={(v) => { setEmail(v); setFormErr('') }} onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            placeholder="you@example.com" error={eErr} valid={!validators.email(email)}
            leftIcon={<MailIcon size={16} />} />
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: JAI_COLORS.navy, fontFamily: JAI_FONTS.tight }}>Password</label>
              <TextLink size={12} onClick={() => navigate('/forgot-password')}>Forgot password?</TextLink>
            </div>
            <PasswordField value={pw} name="password" autoComplete="current-password"
              onChange={(v) => { setPw(v); setFormErr('') }} onBlur={() => setTouched((t) => ({ ...t, pw: true }))}
              placeholder="Enter your password" error={pErr} />
          </div>
        </div>
        <div style={{ margin: '4px 0 20px' }}>
          <Checkbox checked={remember} onChange={setRemember}>Keep me signed in</Checkbox>
        </div>
        <PrimaryButton loading={loading}>{loading ? 'Signing in…' : 'Sign in'}</PrimaryButton>
        <FootSwitch prompt="New to JAI?" action="Create an account" onClick={() => navigate('/signup')} />
      </form>
    </AuthLayout>
  )
}
